import React, { useEffect, useState } from 'react';
import { LogOut, Shield } from 'lucide-react';
import partnerApi, { clearPartnerSession, getPartnerToken, getPartnerUser } from '../lib/partnerApi';
import PartnerGate from '../pages/PartnerGate';

/**
 * Auth gate for /partners/*. Three possible auth states:
 *   - 'admin'      — an admin JWT is in localStorage; lets staff browse all
 *                    trade pages without needing a separate partner login
 *   - 'signed-in'  — partner JWT verified against /api/partners/me
 *   - 'signed-out' — show <PartnerGate />
 *
 * Admin precedence: if BOTH tokens exist (e.g. an admin who also has a
 * partner account) we honour the partner session, because that's the
 * one that was deliberately created for trade-facing content.
 */
function getAdminToken() {
  try { return localStorage.getItem('gl_admin_token'); } catch (e) { return null; }
}

export default function PartnerProtected({ children }) {
  const initial = getPartnerToken() ? 'checking' : (getAdminToken() ? 'admin' : 'signed-out');
  const [state, setState] = useState(initial);
  const [partner, setPartner] = useState(getPartnerUser());

  useEffect(() => {
    if (!getPartnerToken()) {
      setState(getAdminToken() ? 'admin' : 'signed-out');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await partnerApi.get('/partners/me');
        if (!cancelled) {
          setPartner(data);
          setState('signed-in');
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            clearPartnerSession();
            setState(getAdminToken() ? 'admin' : 'signed-out');
          } else {
            // Transient 5xx — keep the stored session rather than dump
            // the visitor back to the gate.
            setState('signed-in');
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function signOut() {
    clearPartnerSession();
    window.location.assign('/partners');
  }

  if (state === 'checking') {
    return (
      <div className="bg-base min-h-[60vh] flex items-center justify-center" data-testid="partner-checking">
        <div className="text-sm text-charcoal/55">Checking your access…</div>
      </div>
    );
  }
  if (state === 'signed-out') return <PartnerGate />;

  if (state === 'admin') {
    return (
      <>
        <div className="bg-heritage text-base text-xs" data-testid="partner-admin-bar">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield size={13} className="text-mustard" />
              <span>Viewing as <strong className="text-mustard">admin</strong> — partners see the login gate here</span>
            </div>
            <a href="/admin" className="hover:text-mustard underline">Back to admin →</a>
          </div>
        </div>
        {children}
      </>
    );
  }

  return (
    <>
      <div className="bg-charcoal text-base/85 text-xs" data-testid="partner-session-bar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-2 flex items-center justify-between gap-4">
          <div>
            Signed in as <strong className="text-mustard">{partner?.business_name || partner?.email}</strong>
            {partner?.business_name && <span className="text-base/60 ml-2 hidden sm:inline">({partner?.email})</span>}
          </div>
          <button
            type="button"
            onClick={signOut}
            data-testid="partner-sign-out"
            className="inline-flex items-center gap-1 hover:text-mustard"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
      {children}
    </>
  );
}
